<template>
  <div class="guide-signer-authentication">
    <h2>What is signer authentication?</h2>
    <p>
      Signer authentication is the process of verifying a signer's identity before they can
      view documents and complete their assigned fields. The platform offers five authentication
      methods, from simple email verification to biometric government-ID checks. Choosing the
      right method balances legal assurance against cost and signer convenience.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Methods at a glance</h2>
    <p>
      Every signer on a package has an authentication method that determines what they must do
      before the signing ceremony begins. The table below summarises all five methods.
    </p>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Method</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">How it works</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Assurance level</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Email</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer clicks a unique, one-time link sent to their email address</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Basic</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Free</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>AccessCode</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer enters a pre-shared secret code before viewing documents</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Basic + shared secret</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Free</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Sms</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer enters a one-time passcode sent to their phone via text message</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Strong (possession factor)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">1 SMS credit</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Kba</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer answers identity-verification questions drawn from public records</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Strong (knowledge factor)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">1 KBA credit</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>IdVerification</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer photographs a government ID and takes a selfie; the system verifies document authenticity and face match</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Highest (biometric)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">1 IDV credit</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h2>Access control vs. identity proofing</h2>
    <p>
      The five methods fall into two fundamentally different categories. Understanding
      the distinction helps you choose the right method for each document.
    </p>
    <p>
      <strong>Access control</strong> methods - Email, AccessCode, and SMS - verify
      that the signer possesses something: an email inbox, a shared secret, or a
      phone. They work like advanced passwords. They confirm that the person at the
      keyboard controls a specific channel, but they do not independently confirm
      <em>who</em> that person is. If someone else has access to the signer's email
      or phone, they could complete the challenge in the signer's place.
    </p>
    <p>
      <strong>Identity proofing</strong> methods - KBA and IdVerification - go
      further. They positively establish the signer's real-world identity by testing
      knowledge that only the named individual should have (KBA) or by matching
      a government-issued photo ID against a live selfie (IdVerification). These
      methods produce an independent evidentiary record that the person who signed
      is who they claim to be, not just that they had access to a device.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Why it matters:</strong> If a signature is ever challenged - "I didn't
      sign that" - access-control methods prove the signing link was opened from the
      right channel, while identity-proofing methods produce affirmative evidence
      tying the signature to a specific person. For documents that could face legal
      scrutiny, identity proofing provides materially stronger standing.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2 id="email">Email authentication</h2>
    <p>
      Email is the default authentication method. The signer receives an email containing a
      unique, cryptographically-generated signing link. Clicking the link proves the signer
      controls the email address and grants access to the signing ceremony.
    </p>
    <p>
      <strong>When to use:</strong> Internal acknowledgements, NDAs between known parties,
      HR onboarding paperwork, low-risk contracts, and any document where email-level
      identity assurance is sufficient.
    </p>
    <p>
      <strong>Signer experience:</strong> The signer receives the notification email, clicks
      the signing link, and is taken directly to the documents. No additional challenge is
      presented.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="access-code">Access code authentication</h2>
    <p>
      Access code authentication requires the signer to enter a pre-shared secret before
      viewing the documents. You define the code when adding the signer, and share it
      out-of-band - via phone call, separate email, or in person. This adds a second factor
      without consuming any credits.
    </p>
    <p>
      <strong>When to use:</strong> Contracts where you want a lightweight second factor
      but do not need formal identity verification. Common in legal and healthcare
      settings where the parties are known but an extra gate is desired.
    </p>
    <p>
      <strong>Signer experience:</strong> After clicking the signing link, the signer sees
      a prompt asking for the access code. They must enter the exact code before the
      documents are revealed.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Tip:</strong> Always share the access code through a different channel than
      the signing notification. For example, send the signing link via email and the access
      code via phone call. This achieves genuine two-factor verification at no credit cost.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2 id="sms">SMS authentication</h2>
    <p>
      SMS authentication sends a one-time passcode (OTP) to the signer's mobile phone via
      text message. The signer enters the code to prove they possess the phone number on
      file. A valid phone number must be provided on the signer record.
    </p>
    <p>
      <strong>When to use:</strong> Financial agreements, loan documents, insurance
      applications, and any workflow where possession-based identity verification is
      required. SMS is often mandated by compliance policies as a second factor.
    </p>
    <p>
      <strong>Signer experience:</strong> After clicking the signing link, the signer sees
      a screen indicating that a code has been sent to their phone number (partially masked
      for security). They enter the code to proceed. If the code expires, they can request
      a new one.
    </p>
    <p>
      <strong>Credit cost:</strong> Each SMS verification attempt consumes one SMS credit
      from the subscription.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="kba">Knowledge-based authentication (KBA)</h2>
    <p>
      KBA generates identity-verification questions from public records - for example,
      "Which of the following addresses have you been associated with?" or "Which lender
      holds your auto loan?" The signer must answer a set of questions correctly within
      a configurable number of attempts.
    </p>
    <p>
      <strong>When to use:</strong> Real estate closings, financial services, legal
      documents, and regulated industries where non-biometric identity proofing is
      required. KBA is the standard required by many US state regulations for remote
      e-signature compliance.
    </p>
    <p>
      <strong>Signer experience:</strong> After clicking the signing link, the signer is
      presented with a series of multiple-choice questions about their personal history.
      They must answer enough questions correctly to pass. If they exhaust their allowed
      attempts, access is denied.
    </p>
    <p>
      <strong>Attempt limits:</strong> You can configure the maximum number of KBA attempts
      per signer using the <code>maxKbaAttempts</code> field. This limits how many times a
      signer can retry the quiz if they fail. The default is determined by your subscription
      settings.
    </p>
    <p>
      <strong>Credit cost:</strong> Each KBA verification attempt consumes one KBA credit
      from the subscription.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="idv">ID verification (IDV)</h2>
    <p>
      ID verification is the highest-assurance method. Powered by Persona.com, it requires
      the signer to photograph a government-issued ID (passport, driver's license, or
      national ID card) and take a live selfie. The system verifies that the document is
      authentic (not forged or digitally altered) and that the selfie matches the photo on
      the ID.
    </p>
    <p>
      <strong>When to use:</strong> Regulatory workflows, high-value transactions, cross-border
      agreements, and any scenario where the strongest possible identity assurance is
      required. Often used alongside or instead of KBA for modern compliance frameworks.
    </p>
    <p>
      <strong>Signer experience:</strong> After clicking the signing link, the signer is
      guided through a mobile-friendly flow: they capture the front (and sometimes back) of
      their government ID, then take a selfie. The verification runs in real time and
      typically completes in under a minute. If verification fails, the signer may retry
      depending on configuration.
    </p>
    <p>
      <strong>Credit cost:</strong> Each IDV verification consumes one IDV credit from the
      subscription.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="stacking">Stacking multiple methods</h2>
    <p>
      For high-assurance workflows, you can require a signer to complete multiple
      authentication methods in sequence before they access the documents. This is
      called <strong>auth stacking</strong>.
    </p>
    <p>
      When multiple methods are stacked, the signer must complete each one in the specified
      order. For example, requiring both <code>Sms</code> and <code>Kba</code> means the
      signer first enters an SMS one-time passcode, then answers KBA questions - only after
      passing both challenges can they view and sign the documents.
    </p>
    <p>
      <strong>How it works:</strong> Set the <code>requiredAuthMethods</code> field to an
      ordered array of method names. When this field is provided, the single
      <code>authMethod</code> field is ignored. The signing ceremony shows progress through
      the stack (for example, "Step 1 of 2").
    </p>
    <p>
      <strong>Credit impact:</strong> Each credit-consuming method in the stack uses its own
      credit independently. A stack of <code>["Sms", "Kba"]</code> consumes one SMS credit
      and one KBA credit per signer.
    </p>

    <q-banner class="bg-amber-1 text-amber-10 q-my-md" rounded>
      <strong>Cost note:</strong> Stacking increases per-signer cost. Use stacking for
      high-risk documents where the additional assurance justifies the credit spend - real
      estate closings, powers of attorney, or high-value financial agreements.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2 id="choosing">Choosing the right method</h2>
    <p>
      Match the authentication level to the legal significance and risk profile of the
      document. Over-authenticating creates friction for signers; under-authenticating
      may leave the signed document vulnerable to challenges.
    </p>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Document type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Recommended method</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Why</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Internal acknowledgements, policies</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Email</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Low risk; signer identity is already established through employment</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">NDAs, vendor agreements</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>AccessCode</code> or <code>Email</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Known parties with moderate risk; access code adds a gate at no cost</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Financial contracts, insurance</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Sms</code> or <code>Kba</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Regulatory expectation of stronger identity verification</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Real estate closings</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Kba</code> or stacked <code>["Sms", "Kba"]</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">State regulations often require KBA for remote closings</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Cross-border, high-value, regulatory</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>IdVerification</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Highest assurance; biometric match against government ID</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h2>Authentication in templates</h2>
    <p>
      Templates support a default authentication method per role. When you define a role
      (for example, "Buyer" or "Witness"), you can set
      <code>defaultAuthMethod</code> and <code>requiredAuthMethods</code> on the role.
      When the template is instantiated into a package, those defaults carry forward to
      the concrete signer mapped to each role.
    </p>
    <p>
      This is useful for standardising compliance requirements. A "Real Estate Buyer" role
      can always default to KBA, while a "CC: Filing" role defaults to Email, regardless
      of who fills those roles on each transaction.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Managing authentication in the portal</h2>
    <p>
      When building a package in the portal, you select the authentication method for each
      signer in the <strong>Signers</strong> panel. The portal surfaces a dropdown with all
      five methods and dynamically shows or hides the access code and phone fields based on
      the selected method. For access code, you enter the code directly. For SMS, you enter
      the signer's phone number.
    </p>
    <p>
      To configure default authentication on a template, open the template in the template
      editor, select the role, and set the authentication method in the role settings panel.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Authentication methods are set when creating or updating signers on packages, and when
      defining roles on templates. Below are the API fields, code examples, and MCP tool
      equivalents for each operation.
    </p>

    <h3>Setting authentication on a signer</h3>
    <p>
      Use the <code>authMethod</code> field when adding a signer to set a single
      authentication method. Valid values are <code>Email</code>, <code>AccessCode</code>,
      <code>Sms</code>, <code>Kba</code>, and <code>IdVerification</code>.
    </p>

    <h3>Email (default)</h3>
    <p>
      When <code>authMethod</code> is omitted, it defaults to <code>Email</code>. No
      additional fields are required.
    </p>
    <GuideCodeTabs :samples="samples.authEmail" />

    <h3>Access code</h3>
    <p>
      Set <code>authMethod</code> to <code>AccessCode</code> and provide the
      <code>accessCode</code> field with the shared secret string.
    </p>
    <GuideCodeTabs :samples="samples.authAccessCode" />

    <h3>SMS</h3>
    <p>
      Set <code>authMethod</code> to <code>Sms</code> and provide the signer's
      <code>phone</code> number in E.164 format (for example, <code>+15125551234</code>).
    </p>
    <GuideCodeTabs :samples="samples.authSms" />

    <h3>KBA</h3>
    <p>
      Set <code>authMethod</code> to <code>Kba</code>. Optionally set
      <code>maxKbaAttempts</code> to limit the number of quiz retries.
    </p>
    <GuideCodeTabs :samples="samples.authKba" />

    <h3>ID verification</h3>
    <p>
      Set <code>authMethod</code> to <code>IdVerification</code>. The signer will be
      redirected to the Persona-powered verification flow during the signing ceremony.
    </p>
    <GuideCodeTabs :samples="samples.authIdv" />

    <q-separator class="q-my-lg" />

    <h3>Stacking methods via the API</h3>
    <p>
      To require multiple authentication steps, set <code>requiredAuthMethods</code> to
      an ordered array. When present, this field takes precedence over
      <code>authMethod</code>. The signer completes each method in array order.
    </p>
    <GuideCodeTabs :samples="samples.authStacked" />
    <p>
      The signing ceremony tracks progress through the stack. If you are building an
      embedded signing experience, the signing URL response includes the current step
      and total so your UI can render a progress indicator.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Setting defaults on template roles</h3>
    <p>
      Template roles accept <code>defaultAuthMethod</code> and
      <code>requiredAuthMethods</code> to pre-configure authentication for every package
      created from the template.
    </p>
    <GuideCodeTabs :samples="samples.templateRole" />

    <q-separator class="q-my-lg" />

    <h3>Updating authentication on a draft signer</h3>
    <p>
      While the package is in <code>Draft</code>, use
      <code>PUT /api/v1/packages/{id}/signers/{signerId}</code> to change the
      authentication method. This is a partial update - only the fields you include are
      modified. Changing to <code>Sms</code> or <code>AccessCode</code> requires the
      corresponding <code>phone</code> or <code>accessCode</code> field.
    </p>
    <GuideCodeTabs :samples="samples.updateAuth" />

    <q-separator class="q-my-lg" />

    <h3>Reassignment resets authentication</h3>
    <p>
      When a signer is reassigned on a sent package via
      <code>POST /api/v1/packages/{id}/signers/{signerId}/reassign</code>, all
      authentication state is cleared - SMS OTP history, KBA attempts, and IDV results
      are reset. The replacement signer starts authentication from scratch. The
      authentication method itself is preserved from the original signer configuration
      unless you specify a new <code>authMethod</code> on the reassignment request.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Authentication fields reference</h3>
    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Field</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>authMethod</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">string</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Single authentication method: <code>Email</code>, <code>AccessCode</code>, <code>Sms</code>, <code>Kba</code>, or <code>IdVerification</code>. Defaults to <code>Email</code>.</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>requiredAuthMethods</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">string[]</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Ordered array of methods the signer must complete in sequence. Overrides <code>authMethod</code> when provided.</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>accessCode</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">string</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">The shared secret the signer must enter. Required when <code>authMethod</code> is <code>AccessCode</code>.</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>phone</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">string</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Phone number in E.164 format. Required when <code>authMethod</code> is <code>Sms</code> or <code>Sms</code> is in <code>requiredAuthMethods</code>.</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>maxKbaAttempts</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">integer</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Maximum number of KBA quiz attempts allowed. Only relevant when using <code>Kba</code> authentication.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h3>MCP tool equivalents</h3>
    <p>
      If you are building with AI agents via the MCP server, the same authentication
      options are available on the signer tools:
    </p>
    <ul>
      <li><code>add_signer</code> - accepts <code>authMethod</code>, <code>accessCode</code>, <code>phone</code>, <code>maxKbaAttempts</code>, and <code>requiredAuthMethods</code></li>
      <li><code>add_signers_bulk</code> - each signer object in the array supports the same fields</li>
      <li><code>update_signer</code> - change authentication on a draft signer</li>
      <li><code>reassign_signer</code> - change identity on a sent signer (resets auth state)</li>
      <li><code>add_template_role</code> - set <code>defaultAuthMethod</code> and <code>requiredAuthMethods</code> on template roles</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Match authentication to document risk.</strong> Use Email for low-risk
        internal documents. Use SMS or KBA for financial and legal contracts. Reserve
        IdVerification for regulatory or cross-border scenarios.
      </li>
      <li>
        <strong>Use templates to enforce compliance.</strong> Set the authentication method
        on template roles so that every package created from the template inherits the
        correct level of verification. This prevents human error when compliance requires
        a specific method.
      </li>
      <li>
        <strong>Share access codes out-of-band.</strong> The access code should travel
        through a different channel than the signing link. Email + phone call achieves
        genuine two-factor verification at no credit cost.
      </li>
      <li>
        <strong>Monitor credit consumption.</strong> SMS, KBA, and IDV each consume credits.
        Track your credit balance on the subscription dashboard and set up auto-refill to
        avoid failed authentication attempts due to insufficient credits.
      </li>
      <li>
        <strong>Stack methods deliberately.</strong> Auth stacking increases both security
        and friction. Stack only when the document's legal or financial significance
        justifies the added signer effort and credit cost.
      </li>
    </ul>

    <q-banner class="bg-green-1 text-green-9 q-mt-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Signers</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas, response models, and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

// Helper to render a JSON-body fragment as a single-tab example.
const jsonBody = (obj) => ({ json: JSON.stringify(obj, null, 2) });

const samples = {
    authEmail: jsonBody({
        name: 'Jane Doe',
        email: 'jane@example.com',
        authMethod: 'Email'
    }),

    authAccessCode: jsonBody({
        name: 'Jane Doe',
        email: 'jane@example.com',
        authMethod: 'AccessCode',
        accessCode: 'SECURE-2026'
    }),

    authSms: jsonBody({
        name: 'Jane Doe',
        email: 'jane@example.com',
        authMethod: 'Sms',
        phone: '+15125551234'
    }),

    authKba: jsonBody({
        name: 'Jane Doe',
        email: 'jane@example.com',
        authMethod: 'Kba',
        maxKbaAttempts: 3
    }),

    authIdv: jsonBody({
        name: 'Jane Doe',
        email: 'jane@example.com',
        authMethod: 'IdVerification'
    }),

    authStacked: jsonBody({
        name: 'Jane Doe',
        email: 'jane@example.com',
        phone: '+15125551234',
        requiredAuthMethods: ['Sms', 'Kba']
    }),

    // ─── Template role with default auth ──────────────────────────────────────
    templateRole: {
        curl: `curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/roles \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "name": "Buyer",
    "defaultAuthMethod": "Kba",
    "maxKbaAttempts": 3,
    "routingOrder": 1
  }'`,

        js: `await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Buyer',
    defaultAuthMethod: 'Kba',
    maxKbaAttempts: 3,
    routingOrder: 1
  })
});`,

        node: `await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Buyer',
    defaultAuthMethod: 'Kba',
    maxKbaAttempts: 3,
    routingOrder: 1
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/templates/{template_id}/roles',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'Buyer',
        'defaultAuthMethod': 'Kba',
        'maxKbaAttempts': 3,
        'routingOrder': 1
    }
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync(
    $"api/v1/templates/{templateId}/roles",
    new
    {
        name = "Buyer",
        defaultAuthMethod = "Kba",
        maxKbaAttempts = 3,
        routingOrder = 1
    });`,

        php: `$client->post("api/v1/templates/{$templateId}/roles", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Buyer',
        'defaultAuthMethod' => 'Kba',
        'maxKbaAttempts' => 3,
        'routingOrder' => 1,
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/templates/#{template_id}/roles")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Buyer',
  defaultAuthMethod: 'Kba',
  maxKbaAttempts: 3,
  routingOrder: 1
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Update auth method on a draft signer ─────────────────────────────────
    updateAuth: {
        curl: `curl -X PUT ${API}/api/v1/packages/$PACKAGE_ID/signers/$SIGNER_ID \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "authMethod": "Kba",
    "maxKbaAttempts": 3
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/\${signerId}\`, {
  method: 'PUT',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    authMethod: 'Kba',
    maxKbaAttempts: 3
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/\${signerId}\`, {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    authMethod: 'Kba',
    maxKbaAttempts: 3
  })
});`,

        python: `requests.put(
    f'${API}/api/v1/packages/{package_id}/signers/{signer_id}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'authMethod': 'Kba',
        'maxKbaAttempts': 3
    }
).raise_for_status()`,

        csharp: `await client.PutAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/{signerId}",
    new
    {
        authMethod = "Kba",
        maxKbaAttempts = 3
    });`,

        php: `$client->put("api/v1/packages/{$packageId}/signers/{$signerId}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'authMethod' => 'Kba',
        'maxKbaAttempts' => 3,
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/signers/#{signer_id}")
req = Net::HTTP::Put.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  authMethod: 'Kba',
  maxKbaAttempts: 3
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    }
};

export default {
    name: 'GuideSignerAuthentication',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
